import { ShieldCheck, Tag, Truck, Wrench } from "lucide-react";

import { formatDollarsRound } from "./pricing";
import type { TypeSummary } from "./types";

// Bridges the full-bleed hero band and the constrained grid below. The
// audit's #1 + #7 critiques were "2 cards reads empty" and "the layout
// whiplashes from full-width hero to narrow grid". A brand-styled
// 4-up stat strip pulled from the served catalog data (no extra fetch)
// gives the eye somewhere to land while reinforcing the value props
// customers care about: rate floor, COI acceptance, delivery, fleet size.
export interface CategoryAtAGlanceProps {
  categoryName: string;
  types: TypeSummary[];
}

export function CategoryAtAGlance({
  categoryName,
  types,
}: CategoryAtAGlanceProps): JSX.Element | null {
  if (types.length === 0) return null;

  const priced = types
    .map((t) => t.from_price_per_week_cents)
    .filter((cents): cents is number => cents != null && cents > 0);
  const lowestCents = priced.length > 0 ? Math.min(...priced) : null;
  const coiCount = types.filter((t) => t.requires_coi).length;
  const lowerName = categoryName.toLowerCase();
  const singular = lowerName.replace(/s$/, "");

  const items: Array<{ icon: typeof Tag; label: string; value: string }> = [
    {
      icon: Wrench,
      label: `${types.length === 1 ? "Class" : "Classes"} available`,
      value: `${types.length} ${types.length === 1 ? singular : lowerName}`,
    },
    {
      icon: Tag,
      label: "Rates start at",
      value: lowestCents != null ? `${formatDollarsRound(lowestCents)} / week` : "Quote on call",
    },
    {
      icon: Truck,
      label: "Delivery",
      value: "Same-day in metro Denver",
    },
    {
      icon: ShieldCheck,
      label: coiCount > 0 ? "Insurance" : "Coverage",
      value:
        coiCount > 0
          ? `Bring your COI on ${coiCount} of ${types.length}`
          : "Damage waiver included",
    },
  ];

  return (
    <section
      aria-label={`${categoryName} at a glance`}
      data-testid="category-at-a-glance"
      className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm md:p-5"
    >
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map(({ icon: Icon, label, value }) => (
          <li key={label} className="flex items-start gap-3">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-brand-red/10 text-brand-red">
              <Icon aria-hidden="true" className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">
                {label}
              </p>
              <p className="mt-0.5 truncate text-sm font-medium text-brand-black">
                {value}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
